import { Button } from './ui';

// =============================================================================
// 담당 그룹이 없는 관리자의 첫 화면 — '실패'가 아니라 '아직 배정 전'.
//
// 2026-09-17 대교: "할당받은 그룹이 없는 경우 플레이리스트에 진입했을 때 플레이리스트, 슬라이드,
// 스케줄을 불러오지 못했다 / 권한이 없어서 요청을 할 수 없다는 안내가 나옴."
//   · 서버는 groupScopeGuard에서 403 'no assigned groups'를 준다. 그건 서버 입장의 사실이지 사람이
//     본 사실이 아니다. 화면에서 이건 시스템 장애가 아니라 조직 관리자가 아직 담당 그룹을 안 정한
//     상태다. 대교의 원칙 그대로: "시스템 실패는 오류지 사람 할 일이 아니다."
//   · 그래서 빨간 오류 배너·console.error 대신 중립 빈 상태로 보여 주고, 두 번째 원칙
//     ("'~해야 합니다' 안내문으로 끝내지 말고 그 행동 버튼을 붙여라")을 따라 지금 할 수 있는
//     행동(다시 확인 · 구성 관리 열기)을 함께 둔다.
//
// 판정은 서버 응답을 기다리지 않고 actor로 한다. /api/device-groups 는 가드의 예외라 담당 그룹이
// 없어도 200으로 내려오고, 그 안의 actor가 enforced·central·groups를 그대로 알려 준다. 화면은
// 이걸 보고 처음부터 요청을 실패로 만들지 않는다. 이미 날아간 요청의 403을 가려내야 할 때만
// isNoAssignedGroupsError를 쓴다.
// =============================================================================

/** 서버 groupScopeGuard가 쓰는 문구. ApiError.code는 응답 본문의 error 값을 그대로 담는다. */
export const NO_ASSIGNED_GROUPS_CODE = 'no assigned groups';

type ScopeActorLike = {
  role: string | null;
  central: boolean;
  enforced: boolean;
  groups: ReadonlyArray<unknown>;
};

/**
 * '아직 담당 그룹이 없다' 상태인가.
 *
 * 그룹 권한을 적용(enforced)한 조직에서만 뜻이 있다. 적용 전에는 모든 관리자가 모든 것을 보므로
 * 담당 그룹이 비어 있어도 화면이 막히지 않는다. 중앙 관리자·조직 관리자는 배정 없이도 전체를 본다.
 * actor를 아직 못 읽었으면(null) 판정을 미룬다 — 로딩 중에 빈 상태를 띄우면 깜빡인다.
 */
export function hasNoAssignedGroups(actor: ScopeActorLike | null | undefined): boolean {
  if (!actor || !actor.enforced) return false;
  if (actor.central) return false;
  if (!actor.role) return false;
  return actor.groups.length === 0;
}

/** 이미 받아 버린 403이 '아직 배정 전'인지. ApiError({status, code}) · Error('403 ...') 양쪽을 본다. */
export function isNoAssignedGroupsError(error: unknown): boolean {
  if (!error) return false;
  const shape = error as { status?: unknown; code?: unknown; message?: unknown };
  if (shape.status === 403 && typeof shape.code === 'string' && shape.code.trim() === NO_ASSIGNED_GROUPS_CODE) return true;
  const text = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return text.includes(NO_ASSIGNED_GROUPS_CODE);
}

/** 화면마다 매뉴얼이 약속한 제목(SCDS-114, 2026-10-01 — 매뉴얼 문구가 기준). */
export const NO_GROUP_TITLES = {
  /** 플레이리스트·라이브러리 — 매뉴얼 0장 "콘텐츠를 만들 수 있는 조건", 7장 참조표 */
  content: '콘텐츠를 만들 수 있는 그룹이 없습니다',
  /** 스케줄 — 매뉴얼 2.2 */
  schedule: '스케줄을 만들거나 고칠 권한이 없습니다',
  /** 구성 관리·그룹 작업 공간 — 매뉴얼 7장 참조표 */
  org: '담당 그룹이 없습니다',
} as const;
/** 본문 — 매뉴얼 7장 "담당 그룹이 없습니다. 조직 관리자에게 그룹 배정을 요청하세요"와 같은 뜻, 0장 문구 그대로. */
export const NO_GROUP_BODY = '조직 관리자에게 담당 그룹을 요청하세요.';

/**
 * 빈 상태 안내. role="status"다 — role="alert"로 두면 스크린 리더가 '오류'처럼 끊고 읽는다.
 *
 * @param title       화면별 제목. NO_GROUP_TITLES 중 하나 — 매뉴얼과 글자가 같아야 한다(SCDS-114).
 * @param onRetry     다시 읽기. 조직 관리자가 방금 배정했을 수 있으니 첫 번째 행동으로 둔다.
 * @param showWorkspaceLink 구성 관리(#/org)로 가는 버튼. 구성 관리 안에서 쓸 때는 끈다.
 */
export function NoAssignedGroupsNotice({ title, onRetry, showWorkspaceLink = true, className }: {
  title: string;
  onRetry?: (() => void) | undefined;
  showWorkspaceLink?: boolean;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div role="status" className={`rounded-md border border-line bg-panel-2 px-5 py-6 text-center ${className ?? ''}`}>
      <p className="text-[15px] font-semibold text-text">{title}</p>
      <p className="mx-auto mt-1.5 max-w-[420px] text-[13px] text-text-dim">{NO_GROUP_BODY}</p>
      {/* 조작부는 늘 같은 자리에 — 버튼 줄은 지울 수 있는 행동이 없어도 사라지지 않는다. */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <Button variant="primary" disabled={!onRetry} onClick={onRetry}
          title={onRetry ? '조직 관리자가 방금 배정했을 수 있습니다. 지금 다시 읽습니다.' : '이 화면에서는 다시 읽기를 제공하지 않습니다.'}>
          다시 확인
        </Button>
        {showWorkspaceLink && (
          // HashRouter라 #경로 링크만으로 SPA 안에서 이동한다(새로고침 없음).
          <a href="#/org" className="inline-flex items-center gap-1.5 rounded border border-line px-3.5 py-2 text-[16px] font-medium text-text hover:border-line-strong hover:bg-panel">
            구성 관리 열기
          </a>
        )}
      </div>
    </div>
  );
}
